import { CONTACT } from '@/lib/contact';
// Centralized lead delivery so no inquiry is silently lost.
//
// Every lead form POSTs here, which forwards to the server /api/lead route
// (honeypot + rate limit + Supabase storage + optional external endpoint). If
// the API is unreachable we fall back to opening the visitor's mail client
// pre-filled with the inquiry, so a lead is never dropped even offline.

export interface SendLeadOptions {
  subject: string;
  fields: Record<string, string | undefined>;
  /** Categorises the lead in storage: 'contact' | 'quote' | 'sample' | 'wholesale' | 'careers'. */
  type?: string;
  /** Honeypot value; leave '' for real submits. Bots that fill it are dropped. */
  honeypot?: string;
  /** mailto fallback recipient. */
  to?: string;
}

export type SendLeadResult = 'ok' | 'rate-limited' | 'invalid' | 'error';

/** Message the server returned with an 'invalid' result, for display in the form. */
export let lastLeadError = '';

const DEFAULT_TO = CONTACT.email;

export async function sendLead({
  subject,
  fields,
  type = 'contact',
  honeypot = '',
  to = DEFAULT_TO,
}: SendLeadOptions): Promise<SendLeadResult> {
  try {
    const res = await fetch('/api/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, type, website: honeypot, fields }),
    });
    if (res.ok) return 'ok';
    if (res.status === 429) return 'rate-limited';
    // A 400 means the server rejected the input itself (audit T10). Surface the
    // reason in the form rather than dropping to the mailto fallback  - opening a
    // mail client would look like success while the enquiry is still unusable.
    if (res.status === 400) {
      try {
        const data = (await res.json()) as { message?: string };
        lastLeadError = data?.message || 'Please check the details you entered.';
      } catch {
        lastLeadError = 'Please check the details you entered.';
      }
      return 'invalid';
    }
    // Other server errors fall through to the mailto fallback below.
  } catch {
    // Network error - fall through to the mailto fallback.
  }

  // Fallback: open the visitor's mail client so the lead reaches us even when
  // the API is unreachable. Browser-only.
  if (typeof window !== 'undefined') {
    const body = Object.entries(fields)
      .map(([label, value]) => `${label}: ${value?.trim() ? value : '-'}`)
      .join('\n');
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    return 'ok';
  }
  return 'error';
}
